import * as React from 'react'
import { EllipsisVertical, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useDeleteContact } from '@/hooks/use-contacts'
import { showError, showSuccess } from '@/lib/toast'
import type { ContactTitle } from '@/lib/schemas/contact.schema'
import { AddContactModal } from './add-contact-modal'
import { ContactDeleteDialog } from './contact-delete-dialog'

interface ContactActionsMenuProps {
  contactId: string
  contactName: string
  companyId?: string
  contactTitle?: ContactTitle
  removeMode: 'delete-contact' | 'remove-assignment'
  onChanged?: () => void
  className?: string
}

export function ContactActionsMenu({
  contactId,
  contactName,
  companyId,
  contactTitle,
  removeMode,
  onChanged,
  className,
}: ContactActionsMenuProps) {
  const [isEditOpen, setIsEditOpen] = React.useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false)

  const deleteContact = useDeleteContact()
  const isPending = deleteContact.isPending

  const handleDelete = async () => {
    try {
      if (removeMode === 'delete-contact') {
        await deleteContact.mutateAsync({ id: contactId })
        showSuccess('Contact removed successfully')
      } else {
        if (!companyId) {
          throw new Error('Missing company id')
        }

        await deleteContact.mutateAsync({
          id: contactId,
          companyId,
        })
        showSuccess('Contact removed from company successfully')
      }

      setIsDeleteOpen(false)
      onChanged?.()
    } catch {
      showError('Failed to remove contact. Please try again.')
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className={
              className ?? 'h-6 w-6 text-primary/70 hover:text-primary'
            }
            aria-label={`Open actions for ${contactName}`}
          >
            <EllipsisVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuItem onClick={() => setIsEditOpen(true)}>
            <Pencil className="size-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setIsDeleteOpen(true)}
            className="text-destructive hover:text-destructive focus:text-destructive"
          >
            <Trash2 className="size-4" />
            Remove
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AddContactModal
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        mode="edit"
        contactId={contactId}
        fixedCompanyId={companyId}
        initialEditCompanyId={companyId}
        initialEditTitle={contactTitle}
        onSuccess={onChanged}
      />

      <ContactDeleteDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        title="Remove Contact"
        description={
          removeMode === 'remove-assignment'
            ? `Are you sure you want to remove "${contactName}" from this company?`
            : `Are you sure you want to remove "${contactName}"? This action cannot be undone.`
        }
        confirmLabel="Remove"
        isPending={isPending}
        onConfirm={handleDelete}
      />
    </>
  )
}
